import { ApiStatusError, ValidationError, HttpErrorType, SpecialErrorType, ErrorScenario, NetworkErrorType } from '@/errors';
import type { Message, Chat, AttachmentContentsResponse, ApiCreateMessageRequest, MessagesResponse } from './api';
import type { AppUpdate } from '@/types';

interface ErrorTestConfig {
  chatId: number;
  statusCode: number;
  errorType: HttpErrorType;
  detail: string;
  title?: string;
  traceId?: string;
  problemType?: string;
  instance?: string;
}

class ErrorTestingService {

  private readonly errorConfigs: ErrorTestConfig[] = [
    // 4xx Client Errors (will show snackbar)
    {
      chatId: 400,
      statusCode: 400,
      errorType: HttpErrorType.BAD_REQUEST,
      detail: 'Invalid request format or missing required fields',
      title: 'Bad Request',
      traceId: 'TEST-400-001',
      problemType: 'validation-error', // RFC 9457: 400 Bad Request maps to validation-error
      instance: '/errors/400/bad-request'
    },
    {
      chatId: 401,
      statusCode: 401,
      errorType: HttpErrorType.UNAUTHORIZED,
      detail: 'Authentication token is invalid or expired',
      title: 'Unauthorized Access',
      traceId: 'TEST-401-001'
    },
    {
      chatId: 403,
      statusCode: 403,
      errorType: HttpErrorType.FORBIDDEN,
      detail: 'You do not have permission to access this chat',
      title: 'Access Forbidden',
      traceId: 'TEST-403-001'
    },
    {
      chatId: 404,
      statusCode: 404,
      errorType: HttpErrorType.NOT_FOUND,
      detail: 'Chat with this ID does not exist or has been deleted',
      title: 'Chat Not Found',
      traceId: 'TEST-404-001'
    },
    {
      chatId: 422,
      statusCode: 422,
      errorType: HttpErrorType.VALIDATION_ERROR,
      detail: 'Request validation failed - check your input data',
      title: 'Validation Error',
      traceId: 'TEST-422-001'
    },
    {
      chatId: 429,
      statusCode: 429,
      errorType: HttpErrorType.RATE_LIMITED,
      detail: 'Too many requests. Please wait before trying again',
      title: 'Rate Limit Exceeded',
      traceId: 'TEST-429-001'
    },

    // 5xx Server Errors (will set error state)
    {
      chatId: 500,
      statusCode: 500,
      errorType: HttpErrorType.INTERNAL_SERVER_ERROR,
      detail: 'An unexpected error occurred on the server',
      title: 'Internal Server Error',
      traceId: 'TEST-500-001'
    },
    {
      chatId: 502,
      statusCode: 502,
      errorType: HttpErrorType.BAD_GATEWAY,
      detail: 'Service temporarily unavailable due to gateway issues',
      title: 'Bad Gateway',
      traceId: 'TEST-502-001'
    },
    {
      chatId: 503,
      statusCode: 503,
      errorType: HttpErrorType.SERVICE_UNAVAILABLE,
      detail: 'Service is temporarily down for maintenance',
      title: 'Service Unavailable',
      traceId: 'TEST-503-001'
    },
    {
      chatId: 504,
      statusCode: 504,
      errorType: HttpErrorType.GATEWAY_TIMEOUT,
      detail: 'Request timed out while waiting for server response',
      title: 'Gateway Timeout',
      traceId: 'TEST-504-001'
    }
  ];

  // Special chat IDs for non-HTTP errors
  private readonly specialErrors = {
    8888: SpecialErrorType.NETWORK, // Network error for chat loading
    9999: SpecialErrorType.NETWORK,      // Network error for messages
    9998: SpecialErrorType.TIMEOUT,      // Request timeout
    9997: SpecialErrorType.AUTH,         // Authentication error
    9996: SpecialErrorType.PARSE,        // JSON parsing error
    9995: SpecialErrorType.VALIDATION    // ValidationError
  };

  // Chat loading error configuration
  private readonly chatErrorConfig = {
    enabled: false,
    errorType: SpecialErrorType.NETWORK,
    httpStatusCode: 500, // Only used if errorType is 'http'
    delay: 1000 // Delay in ms before throwing error
  };

  private fallbackIdCounter = 0;

  /**
   * Development-only logging helper
   * Only logs in development mode to avoid console noise in production
   */
  private devLog(...args: any[]) {
    if (import.meta.env.MODE === 'development') {
      console.log(...args);
    }
  }

  /**
   * Development-only error logging helper
   */
  private devError(...args: any[]) {
    if (import.meta.env.MODE === 'development') {
      console.error(...args);
    }
  }

  constructor() {
    // Initialize with startup configuration
    this.initializeStartupConfig();
  }

  /**
   * Configure startup settings for chat error simulation
   * Modify this method to set your desired default error configuration
   */
  private initializeStartupConfig() {
    // Uncomment and modify one of these lines to enable chat errors at startup:
    //** Example ** 
    //this.setupChatErrorScenario(ErrorScenario.NETWORK);
    // Or use the granular method:
    // this.enableChatError(SpecialErrorType.NETWORK, { delay: 2000 });
    
    // Only log in development mode
    this.devLog('🧪 ErrorTestingService initialized with chat error config:', this.chatErrorConfig);
  }

  private generateChatId(): number {
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
      const values = new Uint32Array(1);
      globalThis.crypto.getRandomValues(values);
      return values[0] % 1000000;
    }

    this.fallbackIdCounter = (this.fallbackIdCounter + 1) % 1000000;
    return (Date.now() + this.fallbackIdCounter) % 1000000;
  }

  private generateMessageId(): string {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }

    if (typeof globalThis.crypto?.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      globalThis.crypto.getRandomValues(bytes);
      return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }

    this.fallbackIdCounter += 1;
    return `fallback-${Date.now()}-${this.fallbackIdCounter}`;
  }

  /**
   * Enable chat loading error simulation
   * @param errorType - Type of error to simulate
   * @param options - Additional options like delay and HTTP status code
   */
  enableChatError(
    errorType: SpecialErrorType,
    options: { delay?: number; httpStatusCode?: number } = {}
  ) {
    this.chatErrorConfig.enabled = true;
    this.chatErrorConfig.errorType = errorType;
    this.chatErrorConfig.delay = options.delay ?? 1000;
    if (errorType === SpecialErrorType.HTTP) {
      this.chatErrorConfig.httpStatusCode = options.httpStatusCode ?? 500;
    }
    this.devLog(`🧪 Chat error simulation enabled:`, this.chatErrorConfig);
  }

  /**
   * Disable chat loading error simulation
   */
  disableChatError() {
    this.chatErrorConfig.enabled = false;
    this.devLog('🧪 Chat error simulation disabled');
  }

  /**
   * Get current chat error configuration
   */
  getChatErrorConfig() {
    return { ...this.chatErrorConfig };
  }

  /**
   * Toggle chat error simulation on/off
   */
  toggleChatError() {
    this.chatErrorConfig.enabled = !this.chatErrorConfig.enabled;
    this.devLog(`🧪 Chat error simulation ${this.chatErrorConfig.enabled ? 'enabled' : 'disabled'}`);
    return this.chatErrorConfig.enabled;
  }

  /**
   * Create a message for error testing purposes
   * Simulates various error scenarios based on message content or chatId
   */
  async createMessage(messageData: ApiCreateMessageRequest): Promise<MessagesResponse> {
    console.log('🧪 Error Testing Service: Creating message', messageData);

    // Check if chatId has a configured error
    if (messageData.ChatId != null) {
      const errorConfig = this.errorConfigs.find(config => config.chatId === messageData.ChatId);
      if (errorConfig) {
        throw new ApiStatusError(
          errorConfig.detail,
          errorConfig.statusCode,
          errorConfig.traceId,
          errorConfig.title,
          '/v1/messages'
        );
      }
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Generate or use existing chatId
    const chatId = messageData.ChatId ?? this.generateChatId();

    // Return mock message creation response
    return [
      {
        id: messageData.Id,
        chatId: chatId,
        text: messageData.Message,
        status: 'sent',
        created_at: new Date().toISOString(),
        favourite: false,
        type: 'user',
        parent_id: null,
        deleted: false,
        metadata: messageData.Metadata || {},
        attachments: messageData.Attachments || []
      },
      {
        id: this.generateMessageId(),
        chatId: chatId,
        text: `🧪 Mock AI response to: ${messageData.Message}`,
        status: 'completed',
        created_at: new Date().toISOString(),
        favourite: false,
        type: 'ai',
        parent_id: messageData.Id,
        deleted: false,
        metadata: messageData.Metadata || {},
        attachments: []
      }
    ];
  }

  async getMessages(chatId: number): Promise<Message[]> {
    console.log(`🧪 Error Testing Service: Testing chat ID ${chatId}`);

    // Check for special error types
    if (chatId in this.specialErrors) {
      return this.handleSpecialError(chatId);
    }

    // Check for configured HTTP errors
    const errorConfig = this.errorConfigs.find(config => config.chatId === chatId);
    if (errorConfig) {
      throw new ApiStatusError(
        errorConfig.detail,
        errorConfig.statusCode,
        errorConfig.traceId,
        errorConfig.title,
        `/v1/messages/${chatId}`
      );
    }

    // Default success response for other chat IDs
    if (chatId === 1) {
      console.log('🧪 Error Testing Service: Returning Normal Chat messages with attachments');
      
      // Normal Chat with mock attachments (similar to useChat.ts pattern)
      const now = new Date();
      const aiResponseTime = new Date(now.getTime() + 2000); // 2 seconds later
      
      // Generate mock attachments for the AI response
      const mockAttachments = [
        {
          id: `att_${Date.now()}_1`,
          created: aiResponseTime.toISOString(),
          source: 'system' as const,
          type: 'image/jpeg'
        },
        {
          id: `att_${Date.now()}_2`,
          created: aiResponseTime.toISOString(),
          source: 'system' as const,
          type: 'image/png'
        },
        {
          id: `att_${Date.now()}_3`,
          created: aiResponseTime.toISOString(),
          source: 'system' as const,
          type: 'image/jpeg'
        },
        {
          id: `att_${Date.now()}_4`,
          created: aiResponseTime.toISOString(),
          source: 'system' as const,
          type: 'image/png'
        }
      ];

      console.log('🧪 Generated mock attachments:', mockAttachments);

      const messages = [
        {
          id: `msg-${chatId}-user`,
          chatId: chatId,
          text: `Hello! This is a user message in the normal chat.`,
          status: 'completed',
          created_at: now.toISOString(),
          favourite: false,
          type: 'user',
          parent_id: null,
          deleted: false,
          metadata: {},
          attachments: []
        },
        {
          id: `msg-${chatId}-ai`,
          chatId: chatId,
          text: `Here are some generated images for you! This AI response includes 4 mock attachments.`,
          status: 'completed',
          created_at: aiResponseTime.toISOString(),
          favourite: false,
          type: 'ai',
          parent_id: `msg-${chatId}-user`,
          deleted: false,
          metadata: {},
          attachments: mockAttachments
        }
      ] as Message[];

      console.log('🧪 Returning messages:', messages);
      return messages;
    }

    return [
      {
        id: `msg-${chatId}-1`,
        chatId: chatId,
        text: `This is a test message for chat ${chatId}`,
        status: 'completed',
        created_at: new Date().toISOString(),
        favourite: false,
        type: 'user',
        parent_id: null,
        deleted: false,
        metadata: {},
        attachments: []
      }
    ] as Message[];
  }

  private async handleSpecialError(chatId: number): Promise<never> {
    const errorType = this.specialErrors[chatId as keyof typeof this.specialErrors];

    switch (errorType) {
      case SpecialErrorType.NETWORK: {
        console.log('🧪 Error Testing Service: Simulating network error for chat loading');
        // Add a small delay to simulate network timeout
        await new Promise(resolve => setTimeout(resolve, 1000));
        const { NetworkError } = await import('@/errors');
        throw new NetworkError(
          'Unable to connect to the server. Please check your internet connection.',
          NetworkErrorType.CONNECTION
        );
      }
      
      case SpecialErrorType.TIMEOUT:
        await new Promise(resolve => setTimeout(resolve, 1000));
        throw new ApiStatusError(
          'Request timeout: Server processing took too long',
          408, // Proper HTTP status
          'TEST-408-001',
          'Request Timeout',
          `/v1/messages/${chatId}`
        );
      
      case SpecialErrorType.AUTH:
        throw new Error('No access token available');
      
      case SpecialErrorType.PARSE:
        throw new SyntaxError('Unexpected token in JSON response');
      
      case SpecialErrorType.VALIDATION:
        throw new ValidationError(
          'Frontend validation failed - this is a test validation error',
          'testField',
          'TEST_VALIDATION_ERROR'
        );
      
      default:
        throw new Error('Unknown test error');
    }
  }

  async getChats(): Promise<Chat[]> {
    console.log('🧪 Error Testing Service: getChats called');
    
    // Check if chat error simulation is enabled
    if (this.chatErrorConfig.enabled) {
      console.log(`🧪 Error Testing Service: Simulating ${this.chatErrorConfig.errorType} error for getChats`);
      
      // Add configured delay
      await new Promise(resolve => setTimeout(resolve, this.chatErrorConfig.delay));
      
      // Throw the configured error type
      switch (this.chatErrorConfig.errorType) {
        case SpecialErrorType.NETWORK: {
          const { NetworkError } = await import('@/errors');
          throw new NetworkError(
            'Unable to connect to the server. Please check your internet connection.',
            NetworkErrorType.CONNECTION
          );
        }
          
        case SpecialErrorType.TIMEOUT:
          throw new ApiStatusError(
            'Request timeout: Server processing took too long while loading chats',
            408, // Proper HTTP status
            'TEST-408-002',
            'Chat Load Timeout',
            '/v1/chats'
          );
          
        case SpecialErrorType.AUTH:
          throw new Error('No access token available for loading chats');
          
        case SpecialErrorType.PARSE:
          throw new SyntaxError('Unexpected token in JSON response while parsing chats');
          
        case SpecialErrorType.VALIDATION: {
          const { ValidationError } = await import('@/errors');
          throw new ValidationError(
            'Chat list validation failed - this is a test validation error',
            'chatListField',
            'TEST_CHAT_VALIDATION_ERROR'
          );
        }
          
        case SpecialErrorType.HTTP:
          throw new ApiStatusError(
            `HTTP ${this.chatErrorConfig.httpStatusCode} error while loading chats`,
            this.chatErrorConfig.httpStatusCode,
            `TEST-CHAT-${this.chatErrorConfig.httpStatusCode}-001`,
            `Chat Loading Error`,
            '/v1/chats'
          );
          
        default:
          throw new Error('Unknown chat loading test error');
      }
    }
    
    const chats = [
      {
        id: 1,
        name: "Normal Chat",
        isPinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false
      },
      {
        id: 8888,
        name: "Test Network Error (Chat Loading)",
        isPinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false
      },
      ...this.errorConfigs.map(config => ({
        id: config.chatId,
        name: `Test ${config.statusCode} - ${config.errorType}`,
        isPinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false
      })),
      {
        id: 9999,
        name: "Test Network Error (Messages)",
        isPinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false
      },
      {
        id: 9998,
        name: "Test Timeout Error",
        isPinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false
      },
      {
        id: 9997,
        name: "Test Auth Error",
        isPinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false
      },
      {
        id: 9995,
        name: "Test ValidationError",
        isPinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false
      }
    ];

    return chats;
  }

  async deleteChat(chatId: number): Promise<void> {
  console.log(`🧪 Error Testing Service: deleteChat called for chatId=${chatId}`);

  // Check if chat error simulation is enabled
  if (this.chatErrorConfig.enabled) {
    console.log(`🧪 Error Testing Service: Simulating ${this.chatErrorConfig.errorType} error for deleteChat`);

    // Add configured delay
    await new Promise(resolve => setTimeout(resolve, this.chatErrorConfig.delay));

    // Throw the configured error type
    switch (this.chatErrorConfig.errorType) {
      case SpecialErrorType.NETWORK: {
        const { NetworkError } = await import('@/errors');
        throw new NetworkError(
          'Unable to connect to the server. Please check your internet connection.',
          NetworkErrorType.CONNECTION
        );
      }

      case SpecialErrorType.TIMEOUT:
        throw new ApiStatusError(
          'Request timeout: Server processing took too long while deleting chat',
          408, // HTTP status
          'TEST-408-DELETE-001',
          'Chat Delete Timeout',
          `/v1/delete-chat-history/${chatId}`
        );

      case SpecialErrorType.AUTH:
        throw new Error('No access token available for deleting chat');

      case SpecialErrorType.PARSE:
        throw new SyntaxError('Unexpected token in JSON response while deleting chat');

      case SpecialErrorType.VALIDATION: {
        const { ValidationError } = await import('@/errors');
        throw new ValidationError(
          'Chat deletion validation failed - this is a test validation error',
          'chatDeleteField',
          'TEST_CHAT_DELETE_VALIDATION_ERROR'
        );
      }

      case SpecialErrorType.HTTP:
        throw new ApiStatusError(
          `HTTP ${this.chatErrorConfig.httpStatusCode} error while deleting chat`,
          this.chatErrorConfig.httpStatusCode,
          `TEST-CHAT-DELETE-${this.chatErrorConfig.httpStatusCode}-001`,
          'Chat Deletion Error',
          `/v1/delete-chat-history/${chatId}`
        );

      default:
        throw new Error('Unknown chat deletion test error');
    }
  }

  // Normal mock delete flow (if error simulation disabled)
  console.log(`🧪 Error Testing Service: Chat ${chatId} deleted (simulated)`);
}

  async renameChat(chatId: number, name: string): Promise<void> {
    console.log(`🧪 Error Testing Service: renameChat called for chatId=${chatId}, name=${name}`);

    if (this.chatErrorConfig.enabled) {
      console.log(`🧪 Error Testing Service: Simulating ${this.chatErrorConfig.errorType} error for renameChat`);

      await new Promise(resolve => setTimeout(resolve, this.chatErrorConfig.delay));

      switch (this.chatErrorConfig.errorType) {
        case SpecialErrorType.NETWORK: {
          const { NetworkError } = await import('@/errors');
          throw new NetworkError(
            'Unable to connect to the server. Please check your internet connection.',
            NetworkErrorType.CONNECTION
          );
        }

        case SpecialErrorType.TIMEOUT:
          throw new ApiStatusError(
            'Request timeout: Server processing took too long while renaming chat',
            408,
            'TEST-408-RENAME-001',
            'Chat Rename Timeout',
            `/v1/chats/${chatId}`
          );

        case SpecialErrorType.AUTH:
          throw new Error('No access token available for renaming chat');

        case SpecialErrorType.PARSE:
          throw new SyntaxError('Unexpected token in JSON response while parsing renamed chat');

        case SpecialErrorType.VALIDATION: {
          const { ValidationError } = await import('@/errors');
          throw new ValidationError(
            'Chat name validation failed - this is a test validation error',
            'chatName',
            'TEST_CHAT_RENAME_VALIDATION_ERROR'
          );
        }

        case SpecialErrorType.HTTP:
          throw new ApiStatusError(
            `HTTP ${this.chatErrorConfig.httpStatusCode} error while renaming chat`,
            this.chatErrorConfig.httpStatusCode,
            `TEST-CHAT-RENAME-${this.chatErrorConfig.httpStatusCode}-001`,
            'Chat Rename Error',
            `/v1/chats/${chatId}`
          );

        default:
          throw new Error('Unknown chat rename test error');
      }
    }

    await new Promise(resolve => setTimeout(resolve, 300));
  }

  async pinChat(chatId: number, isPinned: boolean): Promise<Chat> {
    console.log(`🧪 Error Testing Service: pinChat called for chatId=${chatId}, isPinned=${isPinned}`);

    if (this.chatErrorConfig.enabled) {
      console.log(`🧪 Error Testing Service: Simulating ${this.chatErrorConfig.errorType} error for pinChat`);

      await new Promise(resolve => setTimeout(resolve, this.chatErrorConfig.delay));

      switch (this.chatErrorConfig.errorType) {
        case SpecialErrorType.NETWORK: {
          const { NetworkError } = await import('@/errors');
          throw new NetworkError(
            'Unable to connect to the server. Please check your internet connection.',
            NetworkErrorType.CONNECTION
          );
        }

        case SpecialErrorType.TIMEOUT:
          throw new ApiStatusError(
            'Request timeout: Server processing took too long while updating chat pin state',
            408,
            'TEST-408-PIN-001',
            'Chat Pin Timeout',
            `/v1/chats/${chatId}/pin`
          );

        case SpecialErrorType.AUTH:
          throw new Error('No access token available for updating chat pin state');

        case SpecialErrorType.PARSE:
          throw new SyntaxError('Unexpected token in JSON response while updating chat pin state');

        case SpecialErrorType.VALIDATION: {
          const { ValidationError } = await import('@/errors');
          throw new ValidationError(
            'Chat pin validation failed - this is a test validation error',
            'chatPin',
            'TEST_CHAT_PIN_VALIDATION_ERROR'
          );
        }

        case SpecialErrorType.HTTP:
          throw new ApiStatusError(
            `HTTP ${this.chatErrorConfig.httpStatusCode} error while updating chat pin state`,
            this.chatErrorConfig.httpStatusCode,
            `TEST-CHAT-PIN-${this.chatErrorConfig.httpStatusCode}-001`,
            'Chat Pin Error',
            `/v1/chats/${chatId}/pin`
          );

        default:
          throw new Error('Unknown chat pin test error');
      }
    }

    // Normal mock pin flow (if error simulation disabled)
    console.log(`🧪 Error Testing Service: Chat ${chatId} pin state set to ${isPinned} (simulated)`);

    const nowIso = new Date().toISOString();
    const mappedErrorConfig = this.errorConfigs.find((config) => config.chatId === chatId);
    let chatName = `Chat ${chatId}`;
    if (mappedErrorConfig) {
      chatName = `Test ${mappedErrorConfig.statusCode} - ${mappedErrorConfig.errorType}`;
    } else {
      switch (chatId) {
        case 1:
          chatName = 'Normal Chat';
          break;
        case 8888:
          chatName = 'Test Network Error (Chat Loading)';
          break;
        case 9999:
          chatName = 'Test Network Error (Messages)';
          break;
        case 9998:
          chatName = 'Test Timeout Error';
          break;
        case 9997:
          chatName = 'Test Auth Error';
          break;
        case 9995:
          chatName = 'Test ValidationError';
          break;
      }
    }

    return {
      id: chatId,
      name: chatName,
      isPinned,
      createdAt: nowIso,
      updatedAt: nowIso,
      deleted: false
    };
  }
  async dismissUpdate(id: number): Promise<void> {
    console.log(`🧪 Error Testing Service: dismissUpdate called for id=${id} (simulated no-op)`);
  }

  async getActiveUpdate(): Promise<AppUpdate | null> {
    console.log('🧪 Error Testing Service: getActiveUpdate called (simulated no-op)');
    return null;
  }

  async fetchAttachmentContents(attachmentIds: string[]): Promise<AttachmentContentsResponse> {
    console.log('🧪 Error Testing Service: fetchAttachmentContents called with IDs:', attachmentIds);
    
    const currentChatId = this.getCurrentChatId();
    console.log('🧪 Current chat context:', currentChatId);
    
    // Handle error test chats
    if (currentChatId !== null && currentChatId !== 1) {
      return this.handleErrorTestChatAttachments(currentChatId, attachmentIds);
    }

    // Normal Chat or unknown context - proceed with attachment loading
    return this.loadNormalChatAttachments(attachmentIds);
  }

  private getCurrentChatId(): number | null {
    const currentLocation = (globalThis as typeof globalThis & { location?: Location }).location;
    if (!currentLocation) return null;
    
    // Try to get from URL params first
    const urlParams = new URLSearchParams(currentLocation.search);
    const urlChatId = urlParams.get('chatId');
    if (urlChatId) {
      return Number.parseInt(urlChatId, 10);
    }
    
    // If not in URL, try to get from current path
    const chatPathRegex = /\/chat\/(\d+)/;
    const pathMatch = chatPathRegex.exec(currentLocation.pathname);
    return pathMatch ? Number.parseInt(pathMatch[1], 10) : null;
  }

  private async handleErrorTestChatAttachments(currentChatId: number, attachmentIds: string[]): Promise<AttachmentContentsResponse> {
    console.log(`🧪 Error Testing Service: Blocking attachment load for error test chat ${currentChatId}`);
    
    // Check if this is a configured error test case
    const errorConfig = this.errorConfigs.find(config => config.chatId === currentChatId);
    if (errorConfig) {
      console.log(`🧪 Throwing ${errorConfig.statusCode} error for attachment loading`);
      const attachmentId = attachmentIds.length > 0 ? attachmentIds[0] : 'unknown';
      throw new ApiStatusError(
        `Failed to load attachments: ${errorConfig.detail}`,
        errorConfig.statusCode,
        errorConfig.traceId,
        `Attachment Error - ${errorConfig.title}`,
        `/v1/attachments/${attachmentId}/contents`
      );
    }
    
    // Check for special error types
    if (currentChatId in this.specialErrors) {
      return this.handleSpecialAttachmentError(currentChatId);
    }
    
    // For any other error test chat, return empty
    console.log(`🧪 Returning empty attachments for test chat ${currentChatId}`);
    return [];
  }

  private async loadNormalChatAttachments(attachmentIds: string[]): Promise<AttachmentContentsResponse> {
    console.log('🧪 Error Testing Service: Loading attachments for Normal Chat');
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      return await this.loadAttachmentsWithMockData(attachmentIds);
    } catch (error) {
      this.devError('🧪 Error in fetchAttachmentContents:', error);
      // Fallback to basic mock data only for Normal Chat
      return this.createFallbackAttachments(attachmentIds);
    }
  }

  private async loadAttachmentsWithMockData(attachmentIds: string[]): Promise<AttachmentContentsResponse> {
    try {
      // Try to import raw base64 data from mock file
      const mockImageModule = await import('../mocks/mock_image.txt?raw');
      const mockFileContent = mockImageModule.default;
      const mockBase64Images = mockFileContent.split('\n').filter(line => line.trim().length > 0);
      
      console.log('🧪 Loaded raw base64 from mock file:', mockBase64Images.length, 'images');
      
      // If no valid mock images, use fallback images (valid base64 1x1 pixel PNGs)
    const fallbackImages = [
      // Small red square PNG (1x1 pixel) - valid base64
      'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==',
      // Small green square PNG (1x1 pixel) - valid base64
      'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChAI9jU8jEwAAAABJRU5ErkJggg==',
      // Small blue square PNG (1x1 pixel) - valid base64
      'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
      // Small yellow square PNG (1x1 pixel) - valid base64
      'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='
    ];

    const imagesToUse = mockBase64Images.length > 0 ? mockBase64Images : fallbackImages;
    console.log('🧪 Using images:', imagesToUse.length, 'total');

    const result = attachmentIds.map((id, index) => {
      // Use different mock images for each attachment, cycling through available ones
      const imageIndex = index % imagesToUse.length;
      let base64Content = imagesToUse[imageIndex];

      // Clean the base64 data - remove data URL prefix if present
      if (base64Content?.includes(',')) {
        base64Content = base64Content.split(',')[1];
      }

      console.log(`🧪 Processing attachment ${id} with image ${imageIndex}:`, {
        base64Length: base64Content?.length || 0,
        isValidLength: (base64Content?.length || 0) > 20
      });

      return {
        id,
        encoded_content: base64Content || fallbackImages[0]
      };
    });

    console.log('🧪 Returning attachment contents:', result.map(r => ({ 
      id: r.id, 
      contentLength: r.encoded_content.length
    })));
    return result;
    } catch (error) {
      this.devError('🧪 Error loading mock images, using fallback:', error);
      return this.createFallbackAttachments(attachmentIds);
    }
  }

  private createFallbackAttachments(attachmentIds: string[]): AttachmentContentsResponse {
    return attachmentIds.map((id) => ({
      id,
      encoded_content: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=='
    }));
  }

  private async handleSpecialAttachmentError(chatId: number): Promise<never> {
    const errorType = this.specialErrors[chatId as keyof typeof this.specialErrors];

    switch (errorType) {
      case SpecialErrorType.NETWORK: {
        console.log('🧪 Error Testing Service: Simulating network error for attachments');
        await new Promise(resolve => setTimeout(resolve, 1000));
        const { NetworkError } = await import('@/errors');
        throw new NetworkError(
          'Unable to load attachments. Please check your internet connection.',
          NetworkErrorType.CONNECTION
        );
      }
        
      case SpecialErrorType.TIMEOUT:
        await new Promise(resolve => setTimeout(resolve, 2000));
        throw new ApiStatusError(
          'Request timeout: Attachment processing took too long',
          408, // Proper HTTP status
          'TEST-408-003',
          'Attachment Timeout',
          '/v1/attachments/contents'
        );
      
      case SpecialErrorType.AUTH:
        throw new Error('No access token available for attachments');
      
      case SpecialErrorType.PARSE:
        throw new SyntaxError('Unexpected token in attachment response');
      
      case SpecialErrorType.VALIDATION: {
        const { ValidationError } = await import('@/errors');
        throw new ValidationError(
          'Attachment validation failed - this is a test validation error',
          'attachmentField',
          'TEST_ATTACHMENT_VALIDATION_ERROR'
        );
      }
      
      default:
        throw new Error('Unknown attachment test error');
    }
  }

  // Helper method to quickly setup common chat error scenarios
  setupChatErrorScenario(scenario: ErrorScenario) {
    switch (scenario) {
      case ErrorScenario.NETWORK:
        this.enableChatError(SpecialErrorType.NETWORK, { delay: 1000 });
        break;
      case ErrorScenario.TIMEOUT:
        this.enableChatError(SpecialErrorType.TIMEOUT, { delay: 2000 });
        break;
      case ErrorScenario.AUTH:
        this.enableChatError(SpecialErrorType.AUTH, { delay: 500 });
        break;
      case ErrorScenario.PARSE:
        this.enableChatError(SpecialErrorType.PARSE, { delay: 800 });
        break;
      case ErrorScenario.VALIDATION:
        this.enableChatError(SpecialErrorType.VALIDATION, { delay: 600 });
        break;
      case ErrorScenario.HTTP_400:
        this.enableChatError(SpecialErrorType.HTTP, { delay: 600, httpStatusCode: 400 });
        break;
      case ErrorScenario.HTTP_403:
        this.enableChatError(SpecialErrorType.HTTP, { delay: 700, httpStatusCode: 403 });
        break;
      case ErrorScenario.HTTP_404:
        this.enableChatError(SpecialErrorType.HTTP, { delay: 800, httpStatusCode: 404 });
        break;
      case ErrorScenario.HTTP_500:
        this.enableChatError(SpecialErrorType.HTTP, { delay: 1000, httpStatusCode: 500 });
        break;
      case ErrorScenario.OFF:
        this.disableChatError();
        break;
      default:
        console.warn(`Unknown chat error scenario: ${scenario}`);
    }
  }

  // Helper method to get all available test scenarios
  getAvailableTests() {
    return {
      httpErrors: this.errorConfigs.map(config => ({
        chatId: config.chatId,
        type: `${config.statusCode} - ${config.errorType}`,
        description: config.detail,
        expectedBehavior: config.statusCode >= 500 ? 'Error State' : 'Snackbar'
      })),
      specialErrors: Object.entries(this.specialErrors).map(([chatId, type]) => ({
        chatId: Number.parseInt(chatId, 10),
        type: type.toUpperCase(),
        description: `Test ${type} error`,
        expectedBehavior: 'Error State'
      }))
    };
  }
}

// Conditional instantiation - only create instance if error testing is enabled
// This prevents the class from being instantiated in production builds
function createErrorTestingService(): ErrorTestingService | null {
  const isErrorTestingEnabled = import.meta.env.VITE_USE_ERROR_TESTING === 'true';
  
  if (!isErrorTestingEnabled) {
    return null;
  }
  
  // Only instantiate if error testing is explicitly enabled
  // Security validation errors should propagate to prevent production usage
  return new ErrorTestingService();
}

export const errorTestingService = createErrorTestingService();
